import React, { useState } from 'react';
import { useSelection } from '../contexts/SelectionContext';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, X, Trash2, Eye, Download } from 'lucide-react';
import { generateMultipleWhatsAppLink } from '../utils/whatsapp';
import { exportDiamondsToExcel } from '../utils/excelExport';
import { toast } from 'sonner';

export const SelectionBar = () => {
  var IMGURL = "https://justinndiam.com:8000";
  const { selectedDiamonds, clearSelection, removeDiamond, count } = useSelection();
  const { isAuthenticated } = useAuth();
  const [showDetails, setShowDetails] = useState(false);

  if (count === 0) return null;

  const handleSendWhatsApp = () => {
    const link = generateMultipleWhatsAppLink(selectedDiamonds);
    window.open(link, '_blank');
  };

  const handleDownloadExcel = () => {
    if (!isAuthenticated) {
      toast.error('Please login to download Excel file');
      return;
    }
    exportDiamondsToExcel(selectedDiamonds);
    toast.success(`Downloaded ${count} ${count === 1 ? 'diamond' : 'diamonds'} to Excel`);
  };

  return (
    <>
      {/* Floating Selection Bar */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-secondary text-white shadow-2xl border-2 border-primary px-8 py-4 flex items-center gap-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center font-bold">
            {count}
          </div>
          <span className="font-semibold">
            {count} {count === 1 ? 'Diamond' : 'Diamonds'} Selected
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-2 px-6 py-2 bg-white/10 hover:bg-white/20 transition-colors text-sm uppercase tracking-widest font-bold"
            data-testid="view-selection-btn"
          >
            <Eye size={16} />
            View
          </button>

          {isAuthenticated && (
            <button
              onClick={handleDownloadExcel}
              className="flex items-center gap-2 px-6 py-2 bg-white/10 hover:bg-white/20 transition-colors text-sm uppercase tracking-widest font-bold"
              data-testid="download-excel-btn"
              title="Download Excel"
            >
              <Download size={16} />
              Excel
            </button>
          )}
          
          <button
            onClick={handleSendWhatsApp}
            className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 transition-colors text-sm uppercase tracking-widest font-bold"
            data-testid="inquire-selected-btn"
          >
            <MessageCircle size={18} />
            Inquire via WhatsApp
          </button>

          <button
            onClick={clearSelection}
            className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 transition-colors"
            title="Clear Selection"
            data-testid="clear-selection-btn"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Selection Details Modal */}
      {showDetails && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setShowDetails(false)}>
          <div className="bg-white max-w-4xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-border p-6 flex items-center justify-between">
              <h2 className="font-heading text-2xl font-semibold">Selected Diamonds ({count})</h2>
              <button onClick={() => setShowDetails(false)} className="hover:text-primary transition-colors">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {selectedDiamonds.map((diamond) => (
                <div key={diamond.diamond_id} className="flex gap-6 border border-border p-6">
                  <div className="w-32 h-32 bg-surface flex-shrink-0">
                    {diamond.images && diamond.images.length > 0 ? (
                      <img src={IMGURL + diamond.images[0]} alt={diamond.shape} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <Eye size={32} />
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-semibold mb-2">{diamond.shape} Diamond</h3>
                    {diamond.stock_id && (
                      <p className="text-sm text-muted-foreground mb-3">
                        Stock ID: <span className="font-mono font-semibold text-foreground">{diamond.stock_id}</span>
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Size: <span className="font-semibold">{diamond.size} ct</span></div>
                      <div>Color: <span className="font-semibold">{diamond.colour}</span></div>
                      <div>Clarity: <span className="font-semibold">{diamond.clarity}</span></div>
                      <div>Cut: <span className="font-semibold">{diamond.cut}</span></div>
                      {diamond.certificate && <div>Certificate: <span className="font-semibold">{diamond.certificate}</span></div>}
                      {diamond.lab && <div>Lab: <span className="font-semibold">{diamond.lab}</span></div>}
                    </div>
                    {diamond.images && diamond.images.length > 0 && (
                      <div className="mt-3 text-xs text-muted-foreground">
                        {diamond.images.length} {diamond.images.length === 1 ? 'image' : 'images'}
                        {diamond.videos && diamond.videos.length > 0 && `, ${diamond.videos.length} ${diamond.videos.length === 1 ? 'video' : 'videos'}`}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => removeDiamond(diamond.diamond_id)}
                    className="text-destructive hover:text-destructive/70 transition-colors self-start"
                    title="Remove from selection"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-border p-6 flex justify-between items-center">
              <button
                onClick={clearSelection}
                className="text-destructive hover:underline font-semibold"
              >
                Clear All
              </button>
              <div className="flex gap-3">
                {isAuthenticated && (
                  <button
                    onClick={handleDownloadExcel}
                    className="bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-3 text-sm uppercase tracking-widest font-bold transition-colors flex items-center gap-2"
                  >
                    <Download size={18} />
                    Download Excel
                  </button>
                )}
                <button
                  onClick={handleSendWhatsApp}
                  className="bg-primary text-white hover:bg-primary/90 px-8 py-3 text-sm uppercase tracking-widest font-bold transition-colors flex items-center gap-2"
                >
                  <MessageCircle size={18} />
                  Send Inquiry ({count} {count === 1 ? 'Diamond' : 'Diamonds'})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
